import React, { useContext, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import {
  HomeIcon,
  UsersIcon,
  ShoppingBagIcon,
  CalendarIcon,
  CreditCardIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const AdminLayout = () => {
  const { user, logout, isAdmin, isAdminOrStaff } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  useEffect(() => {
    // Check if user has admin or staff access
    if (!user || !isAdminOrStaff()) {
      navigate('/');
    }
  }, [user, isAdminOrStaff, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon, adminOnly: false, excludeLakeHouse: false },
    { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon, adminOnly: true, excludeLakeHouse: false },
    { name: 'Users', href: '/admin/users', icon: UsersIcon, adminOnly: true, excludeLakeHouse: false },
    { name: 'Menu Items', href: '/admin/menu', icon: ClipboardDocumentListIcon, adminOnly: false, excludeLakeHouse: false },
    { name: 'Orders', href: '/admin/orders', icon: ShoppingBagIcon, adminOnly: false, excludeLakeHouse: false },
    { name: 'Bookings', href: '/admin/bookings', icon: CalendarIcon, adminOnly: false, excludeLakeHouse: true }, // Hidden for Lake House staff
    { name: 'Payments', href: '/admin/payments', icon: CreditCardIcon, adminOnly: false, excludeLakeHouse: false },
  ];

  const filteredNavigation = navigation.filter((item) => {
    // Filter out admin-only items for non-admin users
    if (item.adminOnly && !isAdmin()) {
      return false;
    }
    
    // Filter out excluded items for Lake House staff
    if (item.excludeLakeHouse && user?.role === 'staff' && user?.restaurant_id === 2) {
      return false;
    }
    
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar for mobile */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 bg-gray-800">
          <Link to="/" className="text-xl font-bold text-white hover:text-amber-400 transition-colors">
            Casa Gatchalian
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-gray-400 hover:text-white"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <nav className="mt-5 px-2 space-y-1">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <item.icon
                  className={`mr-3 h-6 w-6 ${
                    isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
                  }`}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-gray-900">
          <div className="flex h-16 items-center px-4 bg-gray-800">
            <Link to="/" className="text-xl font-bold text-white hover:text-amber-400 transition-colors">
              Casa Gatchalian
            </Link>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto">
            <nav className="flex-1 space-y-1 px-2 py-4">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 h-6 w-6 ${
                        isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
                      }`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navbar */}
        <div className="sticky top-0 z-10 flex h-14 sm:h-16 flex-shrink-0 bg-white shadow">
          <button
            type="button"
            className="border-r border-gray-200 px-3 sm:px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <div className="flex flex-1 justify-between px-3 sm:px-4">
            <div className="flex flex-1 items-center">
              <h1 className="text-base sm:text-xl font-semibold text-gray-900 truncate">
                {navigation.find((item) => item.href === location.pathname)?.name || 'Dashboard'}
              </h1>
            </div>
            <div className="ml-2 sm:ml-4 flex items-center space-x-2 sm:space-x-4">
              {/* User info */}
              <div className="hidden sm:flex items-center">
                <div className="ml-3 text-right">
                  <div className="text-sm font-medium text-gray-900">{user?.full_name}</div>
                  <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                </div>
              </div>
              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-amber-600 rounded-md hover:bg-gray-100 whitespace-nowrap"
              >
                <ArrowLeftOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-4 sm:py-6">
          <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
